import { CheckBadgeIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { RegularSelectInput, Show, SwitchInput, TextInput } from "components/core";

import CommentView from "components/cards/comment-view";
import { FC } from "react";
import { FormikProps } from "formik";
import { GetOfferComments_getOfferComments } from "_graphql/queries/kek/__generated__/GetOfferComments";
import { KEKOffer_findSingleOffer } from "_graphql/kek/queries/__generated__/KEKOffer";

export interface ApproveOfferFormProps {
  form: FormikProps<any>;
  offer: KEKOffer_findSingleOffer | undefined
  comments: (GetOfferComments_getOfferComments | null)[];
}

const ApproveOfferForm: FC<ApproveOfferFormProps> = ({
  form,
  offer,
  comments
}) => {
  return (
    <div className="space-y-1"> {/* Removed divide-y for cleaner spacing */}
      {/* Offer Information Section */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100 mb-3">
          Offer Details
        </h3>
        <dl className="space-y-2">
          {/* Display Originally Proposed Percentage */}
          <div className="flex justify-between items-center">
            <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Insurance company:
            </dt>
            <dd className="text-sm font-semibold text-primary-600 dark:text-primary-400">
              {/* Use toFixed for consistent decimal places, handle potential undefined */}
              {offer?.insurer?.insurer_company_name ?? 'N/A'}
            </dd>
          </div>

          {/* Display Maximum Available Percentage */}
          <div className="flex justify-between items-center">
            <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Class of business:
            </dt>
            <dd className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {offer?.classofbusiness?.business_name ?? 'N/A'}
            </dd>
          </div>
        </dl>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          Please review the offer details above. If everything looks correct, you can proceed to approve the offer.
          If you need to make changes, please update the fields accordingly.
        </p>
      </div>
      {/* {JSON.stringify(form.values, null, 2)} */}
      {/* Status Selection Section */}
      <div>
        <RegularSelectInput id={"status"} label={"Approval Status"} {...form} options={[
          {
            label: "Approve",
            value: "APPROVED"
          },
          {
            label: "Request Changes",
            value: "MODIFY"
          },
          {
            label: "Delete",
            value: "DELETE"
          },
        ]} />
      </div>
      <Show if={Boolean(form.values.status === "APPROVED")}>
        <div className="space-y-3 pt-3">
          <div className="rounded-md bg-green-50 p-4 dark:bg-green-500/15 dark:outline dark:outline-green-500/25">
            <div className="flex">
              <div className="shrink-0">
                <CheckBadgeIcon aria-hidden="true" className="size-5 text-green-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800 dark:text-red-200">
                  By approving this offer, both cover and debit notes would be digitally signed and sent to {offer?.insurer?.insurer_company_name} and closing notes will be sent to {offer?.offer_participant?.length} reinsurer(s)
                </h3>
              </div>
            </div>
          </div>
          <SwitchInput id="sendApprovalDocs"
            label="Send Approval Documents"
            description="If enabled, approval documents will be sent to the reinsurers."
            {...form} />
        </div>
      </Show>
      <Show if={Boolean(form.values.status === "MODIFY" || form.values.status === "DELETE")}>
        <div className="pt-3">
          <CommentView form={form} comments={comments} />
        </div>
      </Show>
    </div>
  );
};

export default ApproveOfferForm;
